import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  BetaTheme,
  BetaThemeContextValue,
} from '../../@types/BetaFeature.type';

export const BETA_THEME_STORAGE_KEY = 'jetstream.beta.theme';

const readStoredTheme = (): BetaTheme => {
  try {
    const stored = globalThis.localStorage?.getItem(BETA_THEME_STORAGE_KEY);
    return stored === 'dark' || stored === 'light' ? stored : 'light';
  } catch {
    return 'light';
  }
};

const BetaThemeContext = createContext<BetaThemeContextValue | undefined>(
  undefined,
);

export const BetaThemeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [theme, setTheme] = useState<BetaTheme>(readStoredTheme);

  useEffect(() => {
    try {
      globalThis.localStorage?.setItem(BETA_THEME_STORAGE_KEY, theme);
    } catch {
      // Storage may be unavailable (private mode); theme still works for the session.
    }
  }, [theme]);

  const toggleTheme = useCallback(
    () => setTheme((current) => (current === 'light' ? 'dark' : 'light')),
    [],
  );

  const value = useMemo(
    () => ({ theme, toggleTheme, setTheme }),
    [theme, toggleTheme],
  );

  return (
    <BetaThemeContext.Provider value={value}>
      {children}
    </BetaThemeContext.Provider>
  );
};

export const useBetaTheme = (): BetaThemeContextValue => {
  const context = useContext(BetaThemeContext);
  if (!context) {
    throw new Error('useBetaTheme must be used within a BetaThemeProvider');
  }
  return context;
};
